import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Archive,
    ArchiveRestore,
    ChevronDown,
    MessageCircle,
    MoreHorizontal,
    Trash2,
    Pencil,
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { getOrCreateConversation } from '../../../firebase/conversation.service';
import { formatMessageTime } from '../../../utils/inboxHelpers';

/**
 * Mobile lead card component displaying a single lead.
 * Fully functional: status dropdown persists, context menu actions work.
 */
export const MobileLeadCard = ({
    lead,
    executions = [],
    onClick,
    openMenu,
    toggleMenu,
    onUpdateStatus,
    onArchiveLead,
    onUnarchiveLead,
    onDeleteLead,
    VALID_STATUSES = ['Active', 'Closed', 'Archived', 'Cold'],
}) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSavingStatus, setIsSavingStatus] = useState(false);

    const handleOpenChat = async (e) => {
        e.stopPropagation();
        if (!user?.uid) return;
        try {
            const convo = await getOrCreateConversation(user.uid, lead);
            navigate('/inbox', { state: { openConversationId: convo.id } });
        } catch (err) {
            console.error('Error navigating to chat:', err);
        }
    };

    const handleStatusChange = async (e) => {
        e.stopPropagation();
        const newStatus = e.target.value;
        if (newStatus === lead.status) return;

        setIsSavingStatus(true);
        try {
            if (newStatus === 'Archived') {
                await onArchiveLead?.(lead.id);
            } else {
                if (lead.archived) {
                    await onUnarchiveLead?.(lead.id);
                }
                // Always update status unless we just unarchived to Active
                // (onUnarchiveLead already sets status to Active)
                const skipStatusUpdate = lead.archived && newStatus === 'Active';
                if (!skipStatusUpdate) {
                    await onUpdateStatus?.(lead.id, newStatus);
                }
            }
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setIsSavingStatus(false);
        }
    };

    const handleEdit = (e) => {
        e.stopPropagation();
        onClick(); // Open drawer which is the edit view
    };

    const handleArchive = async (e) => {
        e.stopPropagation();
        try {
            if (lead.archived) {
                await onUnarchiveLead?.(lead.id);
            } else {
                await onArchiveLead?.(lead.id);
            }
        } catch (error) {
            console.error('Error archiving lead:', error);
        }
    };

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const handleDelete = async (e) => {
        e.stopPropagation();
        setIsDeleting(true);
        try {
            await onDeleteLead?.(lead.id);
        } catch (error) {
            console.error('Error deleting lead:', error);
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    return (
        <>
            <div
                className="lead-card bg-white border border-notion-border rounded-xl p-4 shadow-card active:border-stone-300 transition-all cursor-pointer flex flex-col"
                onClick={onClick}
            >
                {/* 1️⃣ Lead Header - Name + Email */}
                <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-sm text-notion-text">{lead.fullName || lead.name}</h3>
                            {lead.archived && (
                                <span className="text-[10px] bg-stone-200 text-stone-500 px-1.5 py-0.5 rounded">Archived</span>
                            )}
                        </div>
                        {lead.email && (
                            <p className="text-xs text-stone-400 truncate mt-0.5">{lead.email}</p>
                        )}
                    </div>

                    <div className="relative">
                        <button
                            className="text-stone-300 hover:text-stone-600 p-2 -mr-2"
                            onClick={(e) => toggleMenu(e, lead.id)}
                        >
                            <MoreHorizontal className="w-5 h-5" />
                        </button>
                        {openMenu === lead.id && (
                            <div className="absolute right-0 top-8 w-36 bg-white border border-notion-border shadow-popover rounded-lg z-30 py-1 text-left origin-top-right">
                                <button
                                    className="w-full text-left px-4 py-2.5 hover:bg-notion-sidebar text-stone-600 text-xs font-medium flex items-center gap-2"
                                    onClick={handleEdit}
                                >
                                    <Pencil className="w-3.5 h-3.5" /> Edit
                                </button>
                                <button
                                    className="w-full text-left px-4 py-2.5 hover:bg-notion-sidebar text-stone-600 text-xs font-medium flex items-center gap-2"
                                    onClick={handleArchive}
                                >
                                    {lead.archived ? (
                                        <>
                                            <ArchiveRestore className="w-3.5 h-3.5" /> Unarchive
                                        </>
                                    ) : (
                                        <>
                                            <Archive className="w-3.5 h-3.5" /> Archive
                                        </>
                                    )}
                                </button>
                                <div className="h-px bg-notion-border my-0.5"></div>
                                <button
                                    className="w-full text-left px-4 py-2.5 hover:bg-red-50 text-red-600 text-xs font-medium flex items-center gap-2"
                                    onClick={handleDeleteClick}
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* 2️⃣ Automation Section - Status badge + name */}
                {executions.length > 0 ? (
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-stone-600 truncate">
                            {executions[0].automationName}
                        </span>
                        {executions.length > 1 && (
                            <span className="text-[9px] text-stone-400">+{executions.length - 1}</span>
                        )}
                        {(() => {
                            const statusLabel = executions[0].status === 'paused' ? 'Paused' : 'Active';
                            const statusColor = executions[0].status === 'paused'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-green-100 text-green-700';
                            return (
                                <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${statusColor}`}>
                                    {statusLabel}
                                </span>
                            );
                        })()}
                        <span className="text-[9px] text-stone-400">
                            {executions[0].currentStepIndex + 1}/{executions[0].totalSteps}
                        </span>
                    </div>
                ) : (
                    <span className="text-xs text-stone-400 mb-2">No Automation</span>
                )}

                {/* 3️⃣ Status Control - Dropdown with persistence */}
                <div className="relative mb-2" onClick={(e) => e.stopPropagation()}>
                    <select
                        value={lead.status || 'Active'}
                        onChange={handleStatusChange}
                        disabled={isSavingStatus}
                        className="w-full text-xs font-medium text-stone-700 bg-white border border-stone-300 rounded px-2 py-1.5 outline-none cursor-pointer appearance-none pr-6 disabled:opacity-50"
                    >
                        {VALID_STATUSES.map(status => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-2 w-3 h-3 text-stone-400 pointer-events-none" />
                </div>

                {/* 4️⃣ Tags Section - Conditional rendering */}
                {(lead.tags || []).length > 0 && (
                    <div className="flex gap-2 flex-wrap mb-2">
                        {lead.tags.map((tag, index) => (
                            <span key={index} className="px-2 py-1 bg-tag-bg rounded text-[10px] text-stone-600 border border-stone-200">
                                {tag}
                            </span>
                        ))}
                    </div>
                )}

                {/* 4.5️⃣ Interested Properties - Conditional rendering */}
                {(lead.interestedProperties || []).length > 0 && (
                    <div className="text-xs text-stone-500 mb-2 truncate">
                        <span className="font-medium">Interest: </span>
                        {(() => {
                            const props = lead.interestedProperties || [];
                            const firstAddress = props[0]?.address || 'Property';
                            if (props.length === 1) {
                                return firstAddress;
                            }
                            return `${firstAddress} (+${props.length - 1})`;
                        })()}
                    </div>
                )}

                {/* 5️⃣ Last Contact + Action Icons */}
                <div className="flex items-center justify-between mt-auto">
                    <span className={`text-[11px] ${lead._lastContactAt ? 'text-stone-400' : 'text-stone-300 italic'}`}>
                        {lead._lastContactAt ? formatMessageTime(lead._lastContactAt) : 'No contact yet'}
                    </span>
                    <button
                        className="p-2 rounded-full bg-stone-100 text-stone-500 hover:bg-stone-200 transition"
                        onClick={handleOpenChat}
                    >
                        <MessageCircle className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-stone-900/30 flex items-center justify-center z-50 p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full">
                        <h3 className="text-lg font-semibold text-stone-800 mb-2">Delete Lead?</h3>
                        <p className="text-sm text-stone-600 mb-4">
                            This will permanently delete <strong>{lead.fullName || lead.name}</strong>. This action cannot be undone.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDeleteConfirm(false);
                                }}
                                className="flex-1 py-3 border border-notion-border rounded-xl text-stone-600 text-sm font-medium hover:bg-stone-50 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MobileLeadCard;
